import TokenService from "./token.service";

// The Makani AI service is a separate deployment (see
// makani-ai-service/docs/ASK_MAKANI_AI_API.md). On staging/production nginx
// fronts it under the `/ai` prefix (location /ai → localhost:8080, prefix
// stripped by the trailing slash in proxy_pass) — plain `/api/ai/*` paths get
// swallowed by the `location /api` → Node backend block and 404 there.
// In dev the service is reached directly on port 8080.
// REACT_APP_ENV is this repo's equivalent of customer-web's VITE_ENV.
export const AI_API_URL =
  process.env.REACT_APP_ENV == "production"
    ? "https://makani.services"
    : process.env.REACT_APP_ENV == "staging" || process.env.REACT_APP_ENV == "development"
      ? "https://test.makani.services"
      : "http://172.16.9.137:8080";

// The AI service returns `{ details: "..." }` error bodies (never secrets),
// so surface `details` when present.
const toApiError = async (response) => {
  let message = `Request failed (${response.status})`;
  try {
    const data = await response.json();
    if (data?.details) message = data.details;
  } catch (error) {
    // Non-JSON error body — keep the generic message.
  }
  const apiError = new Error(message);
  apiError.status = response.status;
  return apiError;
};

const authHeaders = (extra = {}) => {
  const token = TokenService.getLocalAccessToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const request = async (path, { method = "GET", body } = {}) => {
  const response = await fetch(`${AI_API_URL}${path}`, {
    method,
    headers: authHeaders(body !== undefined ? { "Content-Type": "application/json" } : {}),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return null;
  return response.json();
};

const getSuggestedPrompts = () => request("/api/ai/reports/suggested-prompts");

const getConversations = () => request("/api/ai/reports/conversations");

const getConversationMessages = (conversationId) => request(`/api/ai/reports/conversations/${conversationId}/messages`);

const renameConversation = (conversationId, title) =>
  request(`/api/ai/reports/conversations/${conversationId}`, { method: "PATCH", body: { title } });

const deleteConversation = (conversationId) =>
  request(`/api/ai/reports/conversations/${conversationId}`, { method: "DELETE" });

const sendFeedback = (messageId, rating, comment) =>
  request(`/api/ai/reports/messages/${messageId}/feedback`, {
    method: "POST",
    body: comment ? { rating, comment } : { rating },
  });

// Streaming chat (SSE). Each `data:` line is one AiStreamEvent:
//   {type:"token",token}  {type:"result",result:<AiResponse>}  {type:"error",error}
// The `result` event both carries the full structured answer and signals completion.
// Pass an AbortSignal as `signal` to let the user stop the stream mid-answer —
// aborting rejects with an AbortError the caller should treat as "stopped", not failed.
const streamSse = async (path, payload, { onToken, onResult, onError }, signal) => {
  const response = await fetch(`${AI_API_URL}${path}`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json", Accept: "text/event-stream" }),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok || !response.body) throw await toApiError(response);

  let finished = false;
  const handleEvent = (rawEvent) => {
    const data = rawEvent
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).replace(/^ /, ""))
      .join("\n");
    if (!data) return;

    let event;
    try {
      event = JSON.parse(data);
    } catch (error) {
      return; // skip malformed chunks (e.g. keep-alive comments)
    }

    if (event.type === "token" && event.token) {
      onToken?.(event.token);
    } else if (event.type === "result" && event.result) {
      finished = true;
      onResult?.(event.result);
    } else if (event.type === "error") {
      finished = true;
      onError?.(new Error(event.error || "The AI request failed"));
    }
  };

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

    // SSE events are separated by a blank line.
    let separatorIndex;
    while ((separatorIndex = buffer.indexOf("\n\n")) !== -1) {
      handleEvent(buffer.slice(0, separatorIndex));
      buffer = buffer.slice(separatorIndex + 2);
    }
  }
  if (buffer.trim()) handleEvent(buffer);
  if (!finished) onError?.(new Error("The AI stream ended unexpectedly"));
};

// Analytics turn (Ask Makani AI surface) — text only.
const streamChat = ({ conversationId, message }, handlers, signal) =>
  streamSse("/api/ai/reports/chat/stream", { conversationId: conversationId || null, message }, handlers, signal);

// Attachment turn. Per the API doc (§4), file uploads are only accepted by the
// chat surface (/api/ai/chat/stream): attachments = [{ fileName, mediaType,
// base64Data }] — max 5 total (≤3 images, ≤2 PDFs). Same AiResponse envelope;
// `blocks` comes back empty, the answer is in `message`.
const streamAttachmentChat = ({ conversationId, message, attachments }, handlers, signal) =>
  streamSse(
    "/api/ai/chat/stream",
    { conversationId: conversationId || null, message, attachments },
    handlers,
    signal,
  );

// Returns { blob, filename } for a download, or null when the answer has no
// table to export (HTTP 204). `format=pdf` currently answers 501 — the caller
// should show a "coming soon" notice for that status.
const exportMessage = async (messageId, format = "csv") => {
  const response = await fetch(`${AI_API_URL}/api/ai/reports/messages/${messageId}/export?format=${format}`, {
    headers: authHeaders(),
  });
  if (response.status === 204) return null;
  if (!response.ok) throw await toApiError(response);

  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return {
    blob: await response.blob(),
    filename: match ? match[1] : `makani-report-${messageId}.${format}`,
  };
};

const AiService = {
  getSuggestedPrompts,
  getConversations,
  getConversationMessages,
  renameConversation,
  deleteConversation,
  sendFeedback,
  streamChat,
  streamAttachmentChat,
  exportMessage,
};

export default AiService;
